import React, { useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Dimmer, Icon, Loader, Message, Table } from 'semantic-ui-react';
import store from '../../../store';
import './warehouse.admin.scss';

type ImportRow = {
    importId: number;
    productName: string;
    barCode?: string;
    cost?: number | string;
    remaining?: number;
    createdAt?: string;
    note?: string;
};

type BulkError = {
    lineNo: number;
    importId?: number | string;
    error: string;
};

function toCsvCell(value: unknown): string {
    if (value == null) return '';
    return `"${String(value).replace(/"/g, '""')}"`;
}

function downloadBlob(content: string, filename: string) {
    const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

function parseCsvLine(line: string): string[] {
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
            if (ch === '"' && line[i + 1] === '"') {
                current += '"';
                i++;
            } else if (ch === '"') {
                inQuotes = false;
            } else {
                current += ch;
            }
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === ',') {
            cells.push(current);
            current = '';
        } else {
            current += ch;
        }
    }
    cells.push(current);
    return cells;
}

function parseCsv(text: string): string[][] {
    const normalized = text.replace(/^\uFEFF/, '');
    const lines = normalized.split(/\r?\n/).filter((line) => line.trim() !== '');
    return lines.map(parseCsvLine);
}

function normalizeHeader(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, '');
}

function readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
    });
}

export default function AdminWarehouseImportExpirePageComponent() {
    const history = useHistory();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [rows, setRows] = useState<ImportRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [uploadErrors, setUploadErrors] = useState<BulkError[]>([]);

    const apiBase = process.env.REACT_APP_API_URL || '';

    const ensureAuth = (): string | null => {
        const token = localStorage.getItem('adminToken');
        if (!token || token === 'undefined') {
            store.dispatch({ type: 'redirectUrl', data: window.location.pathname });
            history.push('/');
            return null;
        }
        return token;
    };

    const exportCsv = async () => {
        const token = ensureAuth();
        if (!token) return;

        setLoading(true);
        setErrorMsg('');
        setSuccessMsg('');
        setUploadErrors([]);

        try {
            const res = await fetch(apiBase + 'mongol/warehouse-imports/missing-expire-dates', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    Authorization: token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({}),
            });
            if (!res.ok) throw new Error('export failed');
            const data = await res.json();
            const list: ImportRow[] = Array.isArray(data?.rows) ? data.rows : [];
            setRows(list);

            const headers = ['importId', 'productName', 'barCode', 'cost', 'remaining', 'createdAt', 'note', 'expireDate'];
            const csvLines = [
                headers.join(','),
                ...list.map((row) =>
                    [
                        row.importId,
                        row.productName,
                        row.barCode ?? '',
                        row.cost ?? '',
                        row.remaining ?? '',
                        row.createdAt ?? '',
                        row.note ?? '',
                        '',
                    ].map(toCsvCell).join(',')
                ),
            ];
            downloadBlob(csvLines.join('\n'), `warehouse-import-expire-${new Date().toISOString().slice(0, 10)}.csv`);
            setSuccessMsg(`CSV татагдлаа (${list.length} мөр). expireDate баганаа бөглөөд дахин оруулна уу.`);
        } catch {
            setErrorMsg('CSV татах үед алдаа гарлаа.');
        } finally {
            setLoading(false);
        }
    };

    const refreshList = async () => {
        const token = ensureAuth();
        if (!token) return;

        setLoading(true);
        setErrorMsg('');
        try {
            const res = await fetch(apiBase + 'mongol/warehouse-imports/missing-expire-dates', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    Authorization: token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({}),
            });
            if (!res.ok) throw new Error('fetch failed');
            const data = await res.json();
            setRows(Array.isArray(data?.rows) ? data.rows : []);
        } catch {
            setErrorMsg('Жагсаалт татах үед алдаа гарлаа.');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (!file) return;

        const token = ensureAuth();
        if (!token) return;

        setUploading(true);
        setErrorMsg('');
        setSuccessMsg('');
        setUploadErrors([]);

        try {
            const text = await readFileAsText(file);
            const parsed = parseCsv(text);
            if (parsed.length < 2) {
                setErrorMsg('CSV хоосон эсвэл зөвхөн гарчигтай байна.');
                return;
            }

            const header = parsed[0].map(normalizeHeader);
            const importIdIdx = header.indexOf('importid');
            const expireDateIdx = header.indexOf('expiredate');
            if (importIdIdx === -1 || expireDateIdx === -1) {
                setErrorMsg('CSV-д importId болон expireDate багана заавал байна.');
                return;
            }

            const payloadRows: { importId: number; expireDate: string }[] = [];
            for (let i = 1; i < parsed.length; i++) {
                const cells = parsed[i];
                const importIdRaw = (cells[importIdIdx] || '').trim();
                const expireDateRaw = (cells[expireDateIdx] || '').trim();
                if (!importIdRaw && !expireDateRaw) continue;
                if (!importIdRaw || !expireDateRaw) {
                    setErrorMsg(`${i + 1}-р мөр: importId болон expireDate хоёулаа бөглөнө үү.`);
                    return;
                }
                payloadRows.push({ importId: Number(importIdRaw), expireDate: expireDateRaw.slice(0, 10) });
            }

            if (payloadRows.length === 0) {
                setErrorMsg('expireDate бөглөсөн мөр олдсонгүй.');
                return;
            }

            const res = await fetch(apiBase + 'mongol/warehouse-imports/bulk-expire-dates', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    Authorization: token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ rows: payloadRows }),
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setErrorMsg(data?.error || 'CSV оруулах үед алдаа гарлаа.');
                if (Array.isArray(data?.errors)) setUploadErrors(data.errors);
                return;
            }

            const updatedCount = data?.updated ?? 0;
            const errList: BulkError[] = Array.isArray(data?.errors) ? data.errors : [];
            setUploadErrors(errList);
            setSuccessMsg(`Амжилттай шинэчлэгдсэн: ${updatedCount}${errList.length ? `, алдаатай: ${errList.length}` : ''}`);
            await refreshList();
        } catch {
            setErrorMsg('CSV унших үед алдаа гарлаа.');
        } finally {
            setUploading(false);
        }
    };

    React.useEffect(() => {
        refreshList();
    }, []);

    const busy = loading || uploading;

    return (
        <div className="page-admin">
            <section className="admin-warehouse-form warehouse-imports-page">
                <Dimmer active={busy}>
                    <Loader size="medium" />
                </Dimmer>
                <Link to="/mongol/products">
                    <Icon name="angle left" />
                    <span className="underline">Бүтээгдэхүүн рүү буцах</span>
                </Link>
                <div className="width-100 text-center">
                    <h1>Импорт дуусах огноо (CSV)</h1>
                </div>
                <p>
                    Дуусах огноо оруулаагүй, үлдэгдэлтэй өртгийн бүртгэлүүдийг CSV-ээр татаж, Excel-д{' '}
                    <strong>expireDate</strong> баганаа (YYYY-MM-DD) бөглөөд буцаан оруулна.
                </p>

                {errorMsg ? <Message negative content={errorMsg} /> : null}
                {successMsg ? <Message positive content={successMsg} /> : null}

                <div className="warehouse-actions margin-top-20">
                    <Button data-testid="warehouse-import-expire-export" color="blue" onClick={exportCsv} disabled={busy}>
                        <Icon name="download" />
                        CSV татах
                    </Button>
                    <Button
                        data-testid="warehouse-import-expire-upload"
                        color="green"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={busy}
                    >
                        <Icon name="upload" />
                        CSV оруулах
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <Button basic onClick={refreshList} disabled={busy}>
                        <Icon name="refresh" />
                        Жагсаалт шинэчлэх
                    </Button>
                </div>

                {uploadErrors.length > 0 ? (
                    <Message warning>
                        <Message.Header>Алдаатай мөрүүд</Message.Header>
                        <ul>
                            {uploadErrors.map((err, idx) => (
                                <li key={'err-' + idx}>
                                    Мөр {err.lineNo}, importId {err.importId}: {err.error}
                                </li>
                            ))}
                        </ul>
                    </Message>
                ) : null}

                <h3 className="margin-top-20">Дуусах огноо дутуу ({rows.length})</h3>
                <Table striped className="admin-list-table" width={16}>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>importId</Table.HeaderCell>
                            <Table.HeaderCell>Бүтээгдэхүүн</Table.HeaderCell>
                            <Table.HeaderCell>Бар код</Table.HeaderCell>
                            <Table.HeaderCell>Өртөг</Table.HeaderCell>
                            <Table.HeaderCell>Үлдэгдэл</Table.HeaderCell>
                            <Table.HeaderCell>Огноо</Table.HeaderCell>
                            <Table.HeaderCell>Тайлбар</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {rows.map((row) => (
                            <Table.Row key={'missing-' + row.importId}>
                                <Table.Cell data-label="importId">{row.importId}</Table.Cell>
                                <Table.Cell data-label="Бүтээгдэхүүн">{row.productName}</Table.Cell>
                                <Table.Cell data-label="Бар код">{row.barCode || '–'}</Table.Cell>
                                <Table.Cell data-label="Өртөг">{row.cost != null ? row.cost : '–'}</Table.Cell>
                                <Table.Cell data-label="Үлдэгдэл">{row.remaining != null ? row.remaining : '–'}</Table.Cell>
                                <Table.Cell data-label="Огноо">{row.createdAt || '–'}</Table.Cell>
                                <Table.Cell data-label="Тайлбар">{row.note || '–'}</Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
                {rows.length === 0 && !loading ? <p className="margin-top-20">Дуусах огноо дутуу бүртгэл байхгүй.</p> : null}
            </section>
        </div>
    );
}
